import { Link } from 'react-router-dom';
import Seo from '@/components/feature/Seo';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import ContactForm from '@/pages/contact/components/ContactForm';
import { faqs, siteInfo } from '@/mocks/gossip';

export default function ContactPage() {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://www.youhuochigua.com';

  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      name: '联系我们 - 诱惑吃瓜',
      description: '通过在线留言或邮件联系诱惑吃瓜编辑团队，处理内容更正、图集授权与合作事宜。',
      inLanguage: 'zh-CN',
      url: `${origin}/contact`,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: siteInfo.name,
      url: origin,
      email: siteInfo.email,
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer support',
        email: siteInfo.email,
        availableLanguage: ['zh-CN'],
      },
    },
  ];

  return (
    <>
      <Seo
        title="联系我们 - 诱惑吃瓜 | 内容更正、图集授权与合作"
        description="联系诱惑吃瓜编辑团队：提交内容更正与反馈、图集授权合作、希望整理某个红毯场次，或咨询订阅相关问题，我们会在一个工作日内查看。"
        keywords="联系诱惑吃瓜,内容更正,图集授权,合作联系,在线留言"
        type="website"
        jsonLd={structuredData}
      />

      <Breadcrumbs items={[{ label: '首页', path: '/' }, { label: '联系我们' }]} />

      <section className="bg-background-100 py-14 md:py-20">
        <div className="mx-auto max-w-[1240px] px-4 md:px-8">
          <span className="flex items-center gap-2 text-xs font-semibold tracking-[0.24em] text-primary-600">
            <span className="h-3 w-1 rounded-full bg-primary-500" aria-hidden="true" />
            联系我们
          </span>
          <h1 className="mt-3 font-heading text-3xl font-black text-foreground-950 md:text-5xl">
            有话想对我们说
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            无论是内容更正、图集授权、合作洽谈，还是希望我们整理某个场次，都可以通过下面的表单或邮箱联系到编辑团队。
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-[1240px] px-4 py-12 md:px-8 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <ContactForm />
          </div>

          <aside className="lg:col-span-5">
            <div className="flex flex-col gap-6 lg:sticky lg:top-20">
              <div className="rounded-lg border border-background-200 bg-background-100 p-6">
                <h2 className="font-heading text-lg font-bold text-foreground-950">直接联系我们</h2>
                <ul className="mt-4 space-y-4">
                  <li className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-100 text-lg text-primary-600">
                      <i className="ri-mail-line" aria-hidden="true"></i>
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">内容与反馈</p>
                      <a
                        href={`mailto:${siteInfo.email}`}
                        rel="nofollow"
                        className="mt-0.5 block text-sm text-foreground-600 transition-colors hover:text-primary-600"
                      >
                        {siteInfo.email}
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent-100 text-lg text-accent-700">
                      <i className="ri-briefcase-4-line" aria-hidden="true"></i>
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">商务与合作</p>
                      <a
                        href={`mailto:${siteInfo.cooperation}`}
                        rel="nofollow"
                        className="mt-0.5 block text-sm text-foreground-600 transition-colors hover:text-primary-600"
                      >
                        {siteInfo.cooperation}
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-lg text-secondary-700">
                      <i className="ri-time-line" aria-hidden="true"></i>
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">回复时间</p>
                      <p className="mt-0.5 text-sm text-foreground-600">工作日一个工作日内查看并回复</p>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-100 p-6">
                <h2 className="font-heading text-lg font-bold text-foreground-950">提交前先看看</h2>
                <ul className="mt-3 space-y-3">
                  {faqs.slice(0, 3).map((item) => (
                    <li key={item.question} className="flex items-start gap-2.5">
                      <i className="ri-question-line mt-0.5 text-base text-primary-500" aria-hidden="true"></i>
                      <span className="text-sm leading-relaxed text-foreground-700">{item.question}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/faq"
                  className="mt-4 flex h-10 items-center justify-center gap-1 whitespace-nowrap rounded-md border border-background-300 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-50"
                >
                  查看完整常见问题
                </Link>
              </div>

              <div className="rounded-lg bg-primary-950 p-6 text-background-100">
                <h2 className="font-heading text-base font-bold text-background-50">订阅每周精选</h2>
                <p className="mt-2 text-sm leading-relaxed text-background-200/80">
                  想第一时间收到红毯名场面与同框高光合集，可以在页面底部留下邮箱。
                </p>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}